import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectContentWithSearch, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Separator } from './ui/separator';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Plus, Edit, Trash2, Package2, Hash, Scale, AlertTriangle, CheckCircle, ArrowLeft, X, TrendingUp, FileText, Warehouse, Clock, Box, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { RawItem, RawItemCreateRequest, rawItemApiService, RawItemRateHistoryDto } from '../services/rawItemApi';
import { ItemCategory, itemCategoryApiService } from '../services/itemCategoryApi';
import { HsnCode, hsnApiService } from '../services/hsnApi';
import { CreateCategoryDialog } from './CreateCategoryDialog';
import { getMasterPagesConfig } from '../config/uiConfig';
import { CreateHsnDialog } from './CreateHsnDialog';
import { RawItemUpdateRequest } from '../services/rawItemApi';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { GST_UNIT_OPTIONS, SHAPES, TYPES, FINISHING_TYPES, toGstUnitCode } from '../utils/constants';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useBranch } from '../contexts/BranchContext';
import { inventoryReportApiService, HeatNumberConsumptionReportDto } from '../services/inventoryReportApi';
import { grnApiService, GRNDetail } from '../services/grnApi';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';

interface RawItemFormData {
  itemName: string;
  itemUnit: string;
  type?: string;
  sku: string;
  color: string;
  shape: string;
  size: string;
  grade: string;
  weight: number;
  minBufferStock: number;
  applicableIsStd?: string;
  isActive?: boolean;
  itemCategoryId?: number;
  hsnId?: number;
  rmFinishingType?: string;
  igst?: number;
  cgst?: number;
  sgst?: number;
}

export function RawItemInformationForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id: itemId } = useParams<{ id: string }>();
  const isViewMode = searchParams.get('view') === 'true';
  
  const { selectedBranch } = useBranch();
  const [rawItem, setRawItem] = useState<RawItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<ItemCategory[]>([]);
  const [hsnCodes, setHsnCodes] = useState<HsnCode[]>([]);
  const [rateHistory, setRateHistory] = useState<RawItemRateHistoryDto[]>([]);
  const [loadingRateHistory, setLoadingRateHistory] = useState(false);
  
  // New state for inventory and related data
  const [inventoryData, setInventoryData] = useState<HeatNumberConsumptionReportDto[]>([]);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [bomDetails, setBomDetails] = useState<any[]>([]);
  const [loadingBomDetails, setLoadingBomDetails] = useState(false);
  const [heatNumbers, setHeatNumbers] = useState<GRNDetail[]>([]);
  const [loadingHeatNumbers, setLoadingHeatNumbers] = useState(false);
  const [formData, setFormData] = useState<RawItemFormData>({
    itemName: '',
    itemUnit: '',
    type: '',
    sku: '',
    color: '',
    shape: '',
    size: '',
    grade: '',
    weight: 0,
    minBufferStock: 0,
    applicableIsStd: '',
    isActive: true,
    itemCategoryId: undefined,
    hsnId: undefined,
    rmFinishingType: '',
    igst: 0,
    cgst: 0,
    sgst: 0
  });

  // Load categories and HSN codes
  useEffect(() => {
    const loadData = async () => {
      try {
        const [categoriesData, hsnCodesData] = await Promise.all([
          itemCategoryApiService.getItemCategories(),
          hsnApiService.getHsnCodes()
        ]);
        setCategories(categoriesData);
        setHsnCodes(hsnCodesData);
      } catch (error) {
        // console.error('Error loading categories or HSN codes:', error);
      }
    };
    loadData();
  }, []);

  // Populate form when rawItem is provided
  useEffect(() => {
    if (rawItem) {
      setFormData({
        itemName: rawItem.itemName || '',
        itemUnit: toGstUnitCode(rawItem.itemUnit),
        type: rawItem.type || '',
        sku: rawItem.sku || '',
        color: rawItem.color || '',
        shape: rawItem.shape || '',
        size: rawItem.size || '',
        grade: rawItem.grade || '',
        weight: rawItem.weight || 0,
        minBufferStock: rawItem.minBufferStock || 0,
        applicableIsStd: rawItem.applicableIsStd || '',
        isActive: rawItem.isActive ?? true,
        itemCategoryId: rawItem.itemCategory?.id,
        hsnId: rawItem.hsn?.id,
        rmFinishingType: '',
        igst: rawItem.hsn?.currentIgstRate || 0,
        cgst: rawItem.hsn?.currentCgstRate || 0,
        sgst: rawItem.hsn?.currentSgstRate || 0
      });
    }
  }, [rawItem]);

  // Using constants from utils

  const handleInputChange = (field: keyof RawItemFormData, value: string | number | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-populate tax fields when HSN code is selected
    if (field === 'hsnId' && typeof value === 'number') {
      const selectedHsn = hsnCodes.find(hsn => hsn.id === value);
      if (selectedHsn) {
        setFormData(prev => ({
          ...prev,
          igst: selectedHsn.currentIgstRate,
          cgst: selectedHsn.currentCgstRate,
          sgst: selectedHsn.currentSgstRate
        }));
      }
    }
  };

  const handleCategoryCreated = async () => {
    try {
      const updatedCategories = await itemCategoryApiService.getItemCategories();
      setCategories(updatedCategories);
    } catch (error) {
      // console.error('Error refreshing categories:', error);
    }
  };

  const handleHsnCreated = async () => {
    try {
      const updatedHsnCodes = await hsnApiService.getHsnCodes();
      setHsnCodes(updatedHsnCodes);
    } catch (error) {
      // console.error('Error refreshing HSN codes:', error);
    }
  };

  // Load raw item data if editing
  useEffect(() => {
    if (itemId) {
      const loadRawItem = async () => {
        setLoading(true);
        try {
          const item = await rawItemApiService.getRawItemById(itemId);
          setRawItem(item);
        } catch (error) {
          // console.error('Error loading raw item:', error);
          toast.error('Failed to load raw item');
        } finally {
          setLoading(false);
        }
      };
      loadRawItem();
    }
  }, [itemId]);

  // Load rate history when in view mode
  useEffect(() => {
    if (isViewMode && rawItem?.id) {
      const loadRateHistory = async () => {
        setLoadingRateHistory(true);
        try {
          const history = await rawItemApiService.getRateHistory(rawItem.id);
          setRateHistory(history);
        } catch (error) {
          // console.error('Error loading rate history:', error);
          // Don't show error toast as this is optional data
          setRateHistory([]);
        } finally {
          setLoadingRateHistory(false);
        }
      };
      loadRateHistory();
    }
  }, [isViewMode, rawItem?.id]);

  // Load inventory, BOM details, and heat numbers when in view mode
  useEffect(() => {
    if (isViewMode && rawItem?.id && selectedBranch) {
      const loadViewData = async () => {
        // Load inventory data using consumption API
        setLoadingInventory(true);
        try {
          const response = await inventoryReportApiService.getHeatNumberConsumptionReportByBranch(selectedBranch);
          if (response.success && response.data) {
            // Filter for this raw item
            const itemInventory = response.data.filter(
              (item: HeatNumberConsumptionReportDto) => item.rawItemId === rawItem.id
            );
            setInventoryData(itemInventory);
          }
        } catch (error) {
          // console.error('Error loading inventory data:', error);
          setInventoryData([]);
        } finally {
          setLoadingInventory(false);
        }

        // Load BOM details (raw item usage)
        setLoadingBomDetails(true);
        try {
          const bomData = await rawItemApiService.getRawItemUsage(rawItem.id);
          setBomDetails(bomData);
        } catch (error) {
          // console.error('Error loading raw item usage:', error);
          setBomDetails([]);
        } finally {
          setLoadingBomDetails(false);
        }

        // Load heat numbers
        setLoadingHeatNumbers(true);
        try {
          const grnDetails = await grnApiService.searchGRNDetailsByRawItems([rawItem.id], selectedBranch);
          setHeatNumbers(grnDetails);
        } catch (error) {
          // console.error('Error loading heat numbers:', error);
          setHeatNumbers([]);
        } finally {
          setLoadingHeatNumbers(false);
        }
      };
      loadViewData();
    }
  }, [isViewMode, rawItem?.id, selectedBranch]);

  // Calculate inventory KPIs
  const inventoryKPIs = useMemo(() => {
    if (!inventoryData.length) {
      return {
        totalQuantity: 0,
        totalValue: 0,
        agedQuantity: 0,
        agedValue: 0
      };
    }

    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

    let totalQuantity = 0;
    let totalValue = 0;
    let agedQuantity = 0;
    let agedValue = 0;

    inventoryData.forEach((item) => {
      const remainingQtyRaw = item.remainingQuantity || 0;
      const remainingQty = remainingQtyRaw < 0 ? 0 : remainingQtyRaw;
      const unitPrice = item.unitPrice || 0;
      const value = remainingQty * unitPrice;

      totalQuantity += remainingQty;
      totalValue += value;

      // Check if item is aged (older than 6 months)
      if (item.grnDate) {
        const grnDate = new Date(item.grnDate);
        if (grnDate < sixMonthsAgo) {
          agedQuantity += remainingQty;
          agedValue += value;
        }
      }
    });

    return {
      totalQuantity,
      totalValue,
      agedQuantity,
      agedValue
    };
  }, [inventoryData]);

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Format number
  const formatNumber = (num: number | undefined | null) => {
    if (num === undefined || num === null || isNaN(num)) {
      return '0.00';
    }
    return num.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    
    // Validate required fields
    if (!formData.itemCategoryId) {
      toast.error('Please select a category');
      return;
    }
    
    if (!formData.hsnId) {
      toast.error('Please select an HSN code');
      return;
    }
    
    // Ensure category and HSN IDs are numbers (not undefined)
    const itemCategoryId = formData.itemCategoryId;
    const hsnId = formData.hsnId;
    
    // Convert form data to API format
    const apiData: RawItemCreateRequest = {
      itemName: formData.itemName,
      itemUnit: formData.itemUnit,
      type: formData.type,
      sku: formData.sku,
      color: formData.color,
      shape: formData.shape,
      size: formData.size,
      grade: formData.grade,
      weight: formData.weight,
      minBufferStock: formData.minBufferStock,
      applicableIsStd: formData.applicableIsStd,
      isActive: formData.isActive,
      itemCategoryId: itemCategoryId,
      hsnId: hsnId,
      createdBy: 1,
      updatedBy: 1
    };
    
    try {
      if (rawItem && rawItem.id) {
        // Update existing raw item - use RawItemUpdateRequest
        const updateData: RawItemUpdateRequest = {
          id: rawItem.id,
          itemName: formData.itemName,
          itemUnit: formData.itemUnit,
          type: formData.type,
          sku: formData.sku,
          color: formData.color,
          shape: formData.shape,
          size: formData.size,
          grade: formData.grade,
          weight: formData.weight,
          minBufferStock: formData.minBufferStock,
          applicableIsStd: formData.applicableIsStd,
          isActive: formData.isActive,
          itemCategoryId: itemCategoryId,
          hsnId: hsnId,
          createdBy: apiData.createdBy,
          updatedBy: apiData.updatedBy
        };
        await rawItemApiService.updateRawItem(rawItem.id.toString(), updateData);
        toast.success('Raw item updated successfully!');
      } else {
        // Create new raw item
        const createdRawItem = await rawItemApiService.createRawItem(apiData);
        toast.success('Raw item created successfully!');
      }
      
      // Navigate back to listing page
      navigate('/raw-items');
    } catch (error) {
      // console.error('Error saving raw item:', error);
      toast.error('Failed to save raw item');
    }
  };

  const handleCancel = () => {
    navigate('/raw-items');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
          <span>Loading raw item...</span>
        </div>
      </div>
    );
  }

  return (
    <Card className="w-full border-0 shadow-lg bg-white/70 backdrop-blur-sm">
      <CardHeader className="bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-t-lg">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            className="text-white hover:bg-primary-600"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <CardTitle className="text-xl">
              {isViewMode ? 'View Raw Item' : rawItem ? 'Edit Raw Item' : 'Add New Item'}
            </CardTitle>
            <CardDescription className="text-primary-100">
              {isViewMode ? 'View raw item details' : rawItem ? 'Update raw item information' : 'Create a new raw item'}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        <form onSubmit={handleSave} className={isViewMode ? "space-y-4" : "space-y-8"}>
          {/* Basic Information and Tax Information Side by Side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Basic Information Section - Left */}
            <div className="space-y-4">
              <div className="space-y-2">
              <h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">Basic Information</h3>
              <Separator className="bg-gradient-to-r from-primary-200 to-primary-300 h-0.5" />
            </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-gray-600">
                  Category <span className="text-red-500">*</span>
                </Label>
                {isViewMode ? (
                    <p className="text-sm text-gray-900 font-medium">
                      {categories.find(c => c.id === formData.itemCategoryId)?.categoryName || '-'}
                    </p>
                ) : (
                  <div className="flex gap-2">
                      <Select 
                        value={formData.itemCategoryId?.toString() || ''} 
                        onValueChange={(value) => handleInputChange('itemCategoryId', parseInt(value))}
                      >
                      <SelectTrigger className="flex-1 border-primary-200 focus:border-primary-400 focus:ring-primary-400" >
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((category) => (
                          <SelectItem key={category.id} value={category.id.toString()}>
                            {category.categoryName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <CreateCategoryDialog onCategoryCreated={handleCategoryCreated} />
                  </div>
                )}
              </div>

                <div className="space-y-1">
                  <Label className="text-sm font-medium text-gray-600">Item Name</Label>
                {isViewMode ? (
                    <p className="text-sm text-gray-900 font-medium">{formData.itemName || '-'}</p>
                ) : (
                  <Input
                    id="itemName"
                    value={formData.itemName}
                    onChange={(e) => handleInputChange('itemName', e.target.value)}
                    placeholder="Enter item name"
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  />
                )}
              </div>

                <div className="space-y-1">
                  <Label className="text-sm font-medium text-gray-600">SKU</Label>
                  <p className="text-sm text-gray-900 font-medium">{formData.sku || '-'}</p>
              </div>

                <div className="space-y-1">
                  <Label className="text-sm font-medium text-gray-600">Unit</Label>
                {isViewMode ? (
                    <p className="text-sm text-gray-900 font-medium">{formData.itemUnit || '-'}</p>
                ) : (
                    <Select 
                      value={formData.itemUnit} 
                      onValueChange={(value) => handleInputChange('itemUnit', value)}
                    >
                      <SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400" >
                        <SelectValue placeholder="Select unit" />
                      </SelectTrigger>
                      <SelectContent>
                        {GST_UNIT_OPTIONS.map((unit) => (
                          <SelectItem key={unit.code} value={unit.code}>
                            {unit.code} - {unit.description}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {/* Status moved to Basic Info */}
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-gray-600">Status</Label>
                  {isViewMode ? (
                    <div className="flex items-center gap-2">
                      {formData.isActive ? (
                        <>
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span className="text-sm font-medium text-green-600">Active</span>
                        </>
                      ) : (
                        <>
                          <AlertTriangle className="h-4 w-4 text-red-600" />
                          <span className="text-sm font-medium text-red-600">Inactive</span>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <RadioGroup
                        value={formData.isActive ? "active" : "inactive"}
                        onValueChange={(value) => {
                          handleInputChange('isActive', value === 'active');
                        }}
                        className="flex gap-6"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="active" id="active" />
                          <Label htmlFor="active" className="text-sm font-medium text-green-600 cursor-pointer select-none">
                            <span className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4" />
                              Active
                            </span>
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="inactive" id="inactive" />
                          <Label htmlFor="inactive" className="text-sm font-medium text-red-600 cursor-pointer select-none">
                            <span className="flex items-center gap-2">
                              <AlertTriangle className="h-4 w-4" />
                              Inactive
                            </span>
                          </Label>
                        </div>
                      </RadioGroup>
                  </div>
                )}
              </div>
            </div>
          </div>

            {/* Tax Information Section - Right */}
            <div className="space-y-4">
              <div className="space-y-2">
                <h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">Tax Information</h3>
              <Separator className="bg-gradient-to-r from-primary-200 to-primary-300 h-0.5" />
            </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-gray-600">
                    HSN Code <span className="text-red-500">*</span>
                  </Label>
                {isViewMode ? (
                    <p className="text-sm text-gray-900 font-medium">
                      {hsnCodes.find(h => h.id === formData.hsnId)?.hsnCode || '-'}
                    </p>
                  ) : (
                    <div className="flex gap-2">
                      <Select 
                        value={formData.hsnId?.toString() || ''} 
                        onValueChange={(value) => handleInputChange('hsnId', parseInt(value))}
                      >
                        <SelectTrigger className="flex-1 border-primary-200 focus:border-primary-400 focus:ring-primary-400" >
                          <SelectValue placeholder="Select HSN" />
                    </SelectTrigger>
                        <SelectContentWithSearch searchPlaceholder="Search HSN codes...">
                          {hsnCodes.map((hsn) => (
                            <SelectItem key={hsn.id} value={hsn.id.toString()}>
                              {hsn.hsnCode}
                        </SelectItem>
                      ))}
                        </SelectContentWithSearch>
                  </Select>
                      <CreateHsnDialog onHsnCreated={handleHsnCreated} />
                    </div>
                )}
              </div>

                <div className="space-y-1">
                  <Label className="text-sm font-medium text-gray-600">IGST (%)</Label>
                  <p className="text-sm text-gray-900 font-medium">{formData.igst || '-'}</p>
                  </div>

                <div className="space-y-1">
                  <Label className="text-sm font-medium text-gray-600">CGST (%)</Label>
                  <p className="text-sm text-gray-900 font-medium">{formData.cgst || '-'}</p>
              </div>

                <div className="space-y-1">
                  <Label className="text-sm font-medium text-gray-600">SGST (%)</Label>
                  <p className="text-sm text-gray-900 font-medium">{formData.sgst || '-'}</p>
                  </div>
                  </div>
            </div>
              </div>

          {/* Specifications and Inventory Management Side by Side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Specifications Section - Left */}
            <div className="space-y-4">
              <div className="space-y-2">
                <h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">Specifications</h3>
                <Separator className="bg-gradient-to-r from-primary-200 to-primary-300 h-0.5" />
                  </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-gray-600">Size</Label>
                  {isViewMode ? (
                    <p className="text-sm text-gray-900 font-medium">{formData.size || '-'}</p>
                ) : (
                  <Input
                    id="size"
                    value={formData.size}
                    onChange={(e) => handleInputChange('size', e.target.value)}
                    placeholder="Enter size"
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  />
                )}
              </div>

                <div className="space-y-1">
                  <Label className="text-sm font-medium text-gray-600">Grade</Label>
                {isViewMode ? (
                    <p className="text-sm text-gray-900 font-medium">{formData.grade || '-'}</p>
                ) : (
                  <Input
                    id="grade"
                    value={formData.grade}
                    onChange={(e) => handleInputChange('grade', e.target.value)}
                    placeholder="Enter grade"
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  />
                )}
              </div>

                <div className="space-y-1">
                  <Label className="text-sm font-medium text-gray-600">Weight</Label>
                {isViewMode ? (
                    <p className="text-sm text-gray-900 font-medium">{formData.weight || '-'}</p>
                ) : (
                  <Input
                    id="weight"
                    value={formData.weight}
                    onChange={(e) => handleInputChange('weight', parseFloat(e.target.value) || 0)}
                    placeholder="Enter weight"
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  />
                )}
              </div>
            </div>
          </div>

            {/* Inventory Management Section - Right */}
            <div className="space-y-4">
              <div className="space-y-2">
              <h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">Inventory Management</h3>
              <Separator className="bg-gradient-to-r from-primary-200 to-primary-300 h-0.5" />
            </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-gray-600">Min Buffer Stock</Label>
                {isViewMode ? (
                    <p className="text-sm text-gray-900 font-medium">{formData.minBufferStock || '-'}</p>
                ) : (
                  <Input
                    id="minBufferStock"
                    value={formData.minBufferStock}
                    onChange={(e) => handleInputChange('minBufferStock', parseFloat(e.target.value) || 0)}
                    placeholder="Enter minimum buffer stock"
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  />
                )}
              </div>

                <div className="space-y-1">
                  <Label className="text-sm font-medium text-gray-600">Applicable STD</Label>
                {isViewMode ? (
                    <p className="text-sm text-gray-900 font-medium">{formData.applicableIsStd || '-'}</p>
                ) : (
                  <Input
                    id="applicableIsStd"
                    value={formData.applicableIsStd}
                    onChange={(e) => handleInputChange('applicableIsStd', e.target.value)}
                    placeholder="Enter applicable standard"
                    className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                  />
                )}
              </div>
            </div>
          </div>
            </div>


          {/* Rate History and Inventory KPIs Side by Side - Only in View Mode */}
          {isViewMode && rawItem && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Rate History Chart Section - Left */}
              <div className="space-y-4">
                <div className="space-y-2">
                <h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary-700" />
                  Rate History
                </h3>
                <Separator className="bg-gradient-to-r from-primary-200 to-primary-300 h-0.5" />
              </div>

              {loadingRateHistory ? (
                <div className="flex items-center justify-center h-64">
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                    <span>Loading rate history...</span>
                  </div>
                </div>
              ) : rateHistory.length > 0 ? (
                <Card className="border-primary-200 bg-white">
                  <CardContent className="p-6">
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart 
                        data={rateHistory.map(item => ({
                          date: new Date(item.purchaseDate).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          }),
                          unitPrice: item.unitPrice,
                          fullDate: item.purchaseDate,
                          supplierName: item.supplierName || 'N/A',
                          poNumber: item.poNumber || 'N/A',
                          poId: item.poId
                        }))}
                        margin={{ top: 5, right: 30, left: 60, bottom: 80 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis 
                          dataKey="date" 
                          tick={{ fontSize: 12 }}
                          angle={-45}
                          textAnchor="end"
                          height={80}
                        />
                        <YAxis 
                          label={{ value: 'Unit Price (₹)', angle: -90, position: 'left', style: { textAnchor: 'middle' } }}
                          tick={{ fontSize: 12 }}
                          tickFormatter={(value) => `₹${value.toFixed(2)}`}
                          width={80}
                        />
                        <Tooltip 
                          content={({ active, payload }) => {
                            if (active && payload && payload.length > 0) {
                              const data = payload[0].payload;
                              return (
                                <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
                                  <p className="text-sm font-medium text-gray-900 mb-2">
                                    Date: {data.fullDate ? new Date(data.fullDate).toLocaleDateString() : data.date}
                                  </p>
                                  <p className="text-sm text-primary-600 font-semibold mb-1">
                                    Unit Price: ₹{data.unitPrice?.toFixed(2) || '0.00'}
                                  </p>
                                  {data.supplierName && data.supplierName !== 'N/A' && (
                                    <p className="text-sm text-gray-600">
                                      Supplier: {data.supplierName}
                                    </p>
                                  )}
                                  {data.poNumber && data.poNumber !== 'N/A' && (
                                    <p className={`text-sm mt-1 ${data.poId ? 'text-blue-600 font-medium cursor-pointer' : 'text-gray-500'}`}>
                                      PO: {data.poNumber}
                                      {data.poId && <span className="ml-2 text-xs">(Click to view)</span>}
                                    </p>
                                  )}
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="unitPrice" 
                          stroke="#ef4444" 
                          strokeWidth={2}
                          name="Unit Price"
                          dot={((props: any) => {
                            const { cx, cy, payload } = props;
                            if (cx == null || cy == null) {
                              return <g />;
                            }
                            const handleClick = (e: React.MouseEvent) => {
                              e.stopPropagation();
                              if (payload?.poId) {
                                const url = `/purchase-orders/${payload.poId}/view`;
                                window.open(url, '_blank');
                              } else if (payload?.poNumber && payload.poNumber !== 'N/A') {
                                toast.info('PO ID not available. Please navigate manually.');
                              }
                            };
                            return (
                              <circle
                                cx={cx}
                                cy={cy}
                                r={4}
                                fill="#ef4444"
                                strokeWidth={2}
                                stroke="#fff"
                                style={{ cursor: payload?.poId ? 'pointer' : 'default' }}
                                onClick={handleClick}
                              />
                            );
                          }) as any}
                          activeDot={((props: any) => {
                            const { cx, cy, payload } = props;
                            if (cx == null || cy == null) {
                              return <g />;
                            }
                            const handleClick = (e: React.MouseEvent) => {
                              e.stopPropagation();
                              if (payload?.poId) {
                                const url = `/purchase-orders/${payload.poId}/view`;
                                window.open(url, '_blank');
                              } else if (payload?.poNumber && payload.poNumber !== 'N/A') {
                                toast.info('PO ID not available. Please navigate manually.');
                              }
                            };
                            return (
                              <circle
                                cx={cx}
                                cy={cy}
                                r={6}
                                fill="#ef4444"
                                strokeWidth={2}
                                stroke="#fff"
                                style={{ cursor: payload?.poId ? 'pointer' : 'default' }}
                                onClick={handleClick}
                              />
                            );
                          }) as any}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-primary-200 bg-white">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-center h-64 text-gray-500">
                      <div className="text-center">
                        <TrendingUp className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                        <p className="text-sm">No rate history available for this raw item</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              </div>

              {/* Inventory KPIs Section - Right */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent flex items-center gap-2">
                    <Warehouse className="h-5 w-5 text-primary-700" />
                    Inventory Summary
                  </h3>
                  <Separator className="bg-gradient-to-r from-primary-200 to-primary-300 h-0.5" />
                </div>

                {loadingInventory ? (
                  <div className="flex items-center justify-center h-32">
                    <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
                    <span className="ml-2 text-gray-600">Loading inventory data...</span>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Total Inventory KPI */}
                    <Card className="border-primary-200 bg-white">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="p-3 bg-primary-100 rounded-lg">
                              <Package2 className="h-6 w-6 text-primary-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">Total Inventory</p>
                              <p className="text-2xl font-bold text-gray-900 mt-1">
                                {formatNumber(inventoryKPIs.totalQuantity)} {rawItem.itemUnit}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="pt-4 border-t border-gray-200">
                          <p className="text-sm text-gray-600">Total Value</p>
                          <p className="text-xl font-semibold text-primary-600 mt-1">
                            {formatCurrency(inventoryKPIs.totalValue)}
                          </p>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Aged Inventory KPI */}
                    <Card className="border-orange-200 bg-white">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="p-3 bg-orange-100 rounded-lg">
                              <Clock className="h-6 w-6 text-orange-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">Aged Inventory (&gt;6 months)</p>
                              <p className="text-2xl font-bold text-gray-900 mt-1">
                                {formatNumber(inventoryKPIs.agedQuantity)} {rawItem.itemUnit}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="pt-4 border-t border-gray-200">
                          <p className="text-sm text-gray-600">Aged Value</p>
                          <p className="text-xl font-semibold text-orange-600 mt-1">
                            {formatCurrency(inventoryKPIs.agedValue)}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* BOM Details Section - Only in View Mode */}
          {isViewMode && rawItem && (
            <div className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent flex items-center gap-2">
                  <Box className="h-5 w-5 text-primary-700" />
                  Used in Endgoods (BOM Details)
                </h3>
                <Separator className="bg-gradient-to-r from-primary-200 to-primary-300 h-0.5" />
              </div>

              {loadingBomDetails ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
                  <span className="ml-2 text-gray-600">Loading BOM details...</span>
                </div>
              ) : bomDetails.length === 0 ? (
                <Card className="border-primary-200 bg-white">
                  <CardContent className="p-6">
                    <div className="text-center text-gray-500">
                      <Box className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                      <p className="text-sm">This raw item is not used in any BOM</p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-primary-200 bg-white">
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>BOM Number</TableHead>
                          <TableHead>BOM Name</TableHead>
                          <TableHead>Endgood</TableHead>
                          <TableHead className="text-right">Quantity Per Unit</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bomDetails.map((bom, index) => (
                          <TableRow key={bom.id || bom.bomHeader?.id || `bom-${index}`}>
                            <TableCell className="font-medium">
                              {bom.bomHeader?.bomNumber || bom.bomNumber || 'N/A'}
                            </TableCell>
                            <TableCell>{bom.bomHeader?.bomName || bom.bomName || 'N/A'}</TableCell>
                            <TableCell>
                              {bom.endgood?.endgoodName || bom.bomHeader?.endgood?.endgoodName || bom.endgoodName || 'N/A'}
                            </TableCell>
                            <TableCell className="text-right">
                              {(bom.bomDetailQuantity || bom.bomDetailquantity || bom.quantity || bom.qty || 0).toLocaleString('en-IN', {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 20
                              })}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Heat Numbers Section - Only in View Mode */}
          {/* {isViewMode && rawItem && (
            <div className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent flex items-center gap-2">
                  <Hash className="h-5 w-5 text-primary-700" />
                  Heat Numbers
                </h3>
                <Separator className="bg-gradient-to-r from-primary-200 to-primary-300 h-0.5" />
              </div>

              {loadingHeatNumbers ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
                  <span className="ml-2 text-gray-600">Loading heat numbers...</span>
                </div>
              ) : heatNumbers.length === 0 ? (
                <Card className="border-primary-200 bg-white">
                  <CardContent className="p-6">
                    <div className="text-center text-gray-500">
                      <Hash className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                      <p className="text-sm">No heat numbers found for this raw item</p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-primary-200 bg-white">
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Heat Number</TableHead>
                          <TableHead>Supplier Heat No</TableHead>
                          <TableHead>GRN Number</TableHead>
                          <TableHead>GRN Date</TableHead>
                          <TableHead className="text-right">Received Qty</TableHead>
                          <TableHead className="text-right">Remaining Qty</TableHead>
                          <TableHead className="text-right">Unit Price</TableHead>
                          <TableHead>Supplier</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {heatNumbers
                          .filter((detail) => detail.heatNo && detail.heatNo.trim() !== '')
                          .map((detail, index) => (
                            <TableRow key={`${detail.id}-${index}`}>
                              <TableCell className="font-medium">{detail.heatNo || 'N/A'}</TableCell>
                              <TableCell>{detail.supplierHeatNo || 'N/A'}</TableCell>
                              <TableCell>{detail.grn?.grnNumber || 'N/A'}</TableCell>
                              <TableCell>
                                {detail.grn?.grnDate
                                  ? new Date(detail.grn.grnDate).toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric'
                                    })
                                  : 'N/A'}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatNumber(detail.receivedQuantity || 0)}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatNumber((detail as any).remainingQuantity || 0)}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatCurrency(detail.unitPrice || 0)}
                              </TableCell>
                              <TableCell>{detail.grn?.supplier?.supplierName || 'N/A'}</TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </div>
          )} */}

          {/* Audit Information Section - Only in View Mode */}
          {isViewMode && rawItem && (
            <div className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">Audit Information</h3>
                <Separator className="bg-gradient-to-r from-primary-200 to-primary-300 h-0.5" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Created By</Label>
                  <div className="p-3 bg-gray-50 rounded-md border border-gray-200">
                    <span className="text-sm text-gray-900">{rawItem.createdBy}</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Updated By</Label>
                  <div className="p-3 bg-gray-50 rounded-md border border-gray-200">
                    <span className="text-sm text-gray-900">{rawItem.updatedBy}</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Created On</Label>
                  <div className="p-3 bg-gray-50 rounded-md border border-gray-200">
                    <span className="text-sm text-gray-900">
                      {new Date(rawItem.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Updated On</Label>
                  <div className="p-3 bg-gray-50 rounded-md border border-gray-200">
                    <span className="text-sm text-gray-900">
                      {new Date(rawItem.updatedAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Form Actions */}
          {!isViewMode && (
            <div className="flex justify-end gap-4 pt-6 border-t border-primary-200">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                className="border-primary-200 text-primary-600 hover:bg-primary-50"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white"
              >
                {rawItem ? 'Update Raw Item' : 'Create Raw Item'}
              </Button>
            </div>
          )}

          {isViewMode && (
            <div className="flex justify-end gap-4 pt-6 border-t border-primary-200">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                className="border-primary-200 text-primary-600 hover:bg-primary-50"
              >
                Close
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
}